/**
 * harness/tagCloud.js
 * T-Cron-2026-11-01 08:00 早间 — 改进清单 #15「搜索:标签云」
 *
 * 背景(为什么现在做这个):
 *   改进清单 #15 原文 =「搜索:历史 / 拼音 / 标签云」。
 *   · 拼音首字母 —— T99 轮已落地(vite/src/harness/pinyin.js)。
 *   · 历史     —— T-Cron-2026-10-24 08:00 已落地(vite/src/harness/searchHistory.js)。
 *   · 标签云   —— searchHistory.js 的文件头明确写着「数据侧已有 tagList,
 *     本轮不做」 ⇒ **这是清单 #15 唯一剩下的缺口**。
 *
 * 🔴 本轮的核心发现(实测数据,不是拍脑袋):
 *   标签分布是**极端长尾**的。线上 1496 个版本、2373 个不同标签,
 *   按出现次数排前 25 之后是:
 *        读书笔记 526 / 读书 171 / 认知升级 129 / 读书分享 126 / 人工智能 110
 *        … … 比亚迪 39
 *   ⇒ 前 25 名里,最大 526、最小 39,**相差 13.5 倍**。
 *
 *   🔴 **如果按线性比例映射字号,信息量是 0:**
 *        linear scale ⇒ tier3: 1 个(526)
 *                        tier1: 24 个(171 … 39)
 *     也就是说 25 颗 chip 里有 24 颗**长得一模一样**,唯一的大 chip 就是
 *     「读书笔记」—— 因为它一个人占了 526/1496 ≈ 35%,线性映射直接被它
 *     一个人拉爆,剩下 24 颗全部压到最小档。
 *     ⇒ 这就是为什么「标签云」如果照搬线性实现,做出来和现在的
 *       等大 chip 列表**没有任何区别**,纯属白干一轮。
 *
 *   ✅ **对数刻度才是正确的映射**:
 *        log scale ⇒ tier3: 1 个(526)
 *                    tier2: 4 个(171 / 129 / 126 / 110)
 *                    tier1: 20 个(76 … 39)
 *     对数把 13.5 倍的差距压成 3 个可读的档位,而且**保留了排序信息**:
 *     「读书笔记」是绝对主力、「认知升级 / 读书分享 / 人工智能」是第二梯队、
 *     其余 20 个是长尾 —— 这正是用户选材时想知道的东西。
 *
 *   为什么不用开方(sqrt):sqrt 只切出 2 档(1 + 1 + 23),
 *   23 颗仍然同档 ⇒ 和线性一样没有区分度。**实测三种刻度后选 log**。
 *
 * 设计契约:
 *   · **纯函数,无副作用** —— 输入 tagList,输出带 tier 的新数组。
 *   · **只在列表里相对比较** —— tier 是「在这个列表里的相对热度」,
 *     不是绝对数值。绝对值仍然通过 aria-label / title 暴露给读屏和鼠标。
 *   · **不新增筛选语义** —— 点云里的标签和点普通标签芯片效果完全一样,
 *     只是视觉权重不同。已有的 aria-pressed 语义保持不变。
 *   · **数量太少时退化为单档** —— 只有 1~2 个标签时 log(0) 是 -Inf,
 *     必须特判,否则会算出 NaN 字号。
 *
 * 与既有约定的一致性:
 *   · 本文件是 .js ⇒ **必须显式 import React**?—— 不需要:
 *     本模块是纯函数,不含 JSX 也不含 React API。
 *     (harness 里 useLongPress.js / pinyin.js 同样是不 import React 的纯模块。)
 */

import { buzz } from './haptics.js';

/** 标签云的最大档位数(3 档:大 / 中 / 小) */
export const TIER_COUNT = 3;

/**
 * 计算一个标签在整个列表里的相对热度档位。
 *
 * @param {number} value   该标签的出现次数
 * @param {number} min     列表里的最小次数
 * @param {number} max     列表里的最大次数
 * @returns {number} 1(最小) ~ TIER_COUNT(最大)
 */
export function tierOf(value, min, max) {
  if (!isFinite(value) || max <= min) return 1;
  // 对数刻度:把 [min, max] 的比值跨度压成 [0, 1] 的线性刻度。
  const t = (Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min));
  if (!isFinite(t)) return 1;
  const tier = Math.floor(t * TIER_COUNT) + 1;
  return Math.min(TIER_COUNT, Math.max(1, tier));
}

/**
 * 给标签列表加上档位信息。
 *
 * @param {Array<{name: string, count: number}>} tagList
 * @returns {Array<{name: string, count: number, tier: number}>}
 *          新数组(不改动入参),按 count 降序(入参本来就降序,这里再排一次
 *          以防调用方传进来的顺序不是按热度排的 —— 档位依赖「相对位置」,
 *          顺序错了档位就没意义了)。
 */
export function withTiers(tagList) {
  if (!Array.isArray(tagList) || tagList.length === 0) return [];

  const clean = tagList
    .filter((t) => t && t.name)
    .map((t) => ({ name: t.name, count: Number(t.count) || 0 }))
    .sort((a, b) => b.count - a.count);

  const counts = clean.map((t) => t.count);
  const min = Math.min.apply(null, counts);
  const max = Math.max.apply(null, counts);

  return clean.map((t) => ({ ...t, tier: tierOf(t.count, min, max) }));
}

/**
 * 标签云的档位文案(读屏可读)。
 * 不直接念「档位 2」—— 用户需要知道的是**大概有多少篇**,而不是一个内部概念。
 */
export function tierHint(tier, count) {
  if (tier >= TIER_COUNT) return `热门标签，${count} 篇，使用最多`;
  if (tier === TIER_COUNT - 1) return `常用标签，${count} 篇`;
  return `标签，${count} 篇`;
}

/**
 * React 侧的标签云渲染组件工厂 —— 放在 harness 里是为了让
 * 桌面侧栏(Sidebar)和移动端筛选抽屉(MobileFilterDrawer)
 * **共用同一套档位计算**,避免两边算出不同的档位。
 *
 * 注意:本函数返回的是一个「渲染函数」而不是组件,是为了让调用方
 * 直接在各自的 DOM 结构里插入(桌面是 .chip-list,移动端是
 * .mobile-filter-chips,容器类名和 chip 类名都不同)。
 *
 * @param {Object} opts
 * @param {Array} opts.tagList      原始 tagList
 * @param {string} opts.activeTag   当前选中的标签
 * @param {(name: string) => void} opts.onSelect  选中回调
 * @param {string} opts.chipClass   chip 的 className 前缀(不含选中态)
 * @param {string} opts.countSuffix 计数单位(默认「篇」)
 * @returns {Array} 可直接 map 的元素数组
 */
export function renderTagCloud(opts) {
  const {
    tagList,
    activeTag,
    onSelect,
    chipClass = '',
    countSuffix = '篇',
    showCount = true,
  } = opts || {};

  const tiers = withTiers(tagList);
  if (tiers.length === 0) return [];

  return tiers.map((t) => {
    const isActive = activeTag === t.name;
    const cls = `${chipClass} tag-cloud-chip tier-${t.tier}${isActive ? ' active' : ''}`.trim();
    const label = `${t.name}，${t.count}${countSuffix}`;
    return {
      key: t.name,
      name: t.name,
      count: t.count,
      tier: t.tier,
      isActive,
      className: cls,
      ariaLabel: label,
      // 触感反馈:标签芯片是高频点按目标,和侧栏其它 chip 一样给一次
      // 8ms 轻震。buzz() 内部已经处理了「设备不支持」和
      // 「用户开了减弱动态效果」两种情况 ⇒ 这里不需要额外判断。
      onClick: () => {
        buzz(8);
        if (onSelect) onSelect(t.name);
      },
    };
  });
}

const TagCloud = {
  TIER_COUNT,
  tierOf,
  withTiers,
  tierHint,
  renderTagCloud,
};

if (typeof window !== 'undefined') window.TagCloud = TagCloud;

export default TagCloud;
